import { AlertTriangle } from "lucide-react";
import type { WeeklyPlanActivationPreview } from "../../../shared/weekly-planning";
const dateTime = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/Istanbul",
});
type ComparisonRow = WeeklyPlanActivationPreview["includedRows"][number];
function comparisonLine(row: ComparisonRow | null) {
  if (!row) return "Yok";
  const schedule =
    row.scheduledStartAt && row.scheduledEndAt
      ? `${dateTime.format(new Date(row.scheduledStartAt))}–${dateTime.format(new Date(row.scheduledEndAt))}`
      : "zaman belirsiz";
  const distribution = row.allocations.length
    ? row.allocations
        .map(
          (allocation) =>
            `${allocation.requiredAt}: ${allocation.quantity} ${row.unit ?? ""}, ${allocation.plannedHours} saat, +${allocation.startOffsetHours}`,
        )
        .join("; ")
    : "günlük dağılım yok";
  return `${row.kind === "PRODUCTION" ? "Üretim" : "Etkinlik"} · ${row.sourceRow ? `Excel satır ${row.sourceRow} · ` : ""}${row.lineName} · sıra ${row.sequence} · ${row.workOrderNo ?? "iş emri yok"} · ${row.productCode ?? "kod yok"} · toplam ${row.quantity ?? "miktar boş"} ${row.unit ?? ""} · ${row.capacityPerHour ?? "kapasite boş"}/saat · toplam süre ${row.plannedHours ?? "belirsiz"} saat · bu hafta ${row.scheduledQuantity ?? "miktar yok"} ${row.unit ?? ""}, ${row.scheduledHours ?? "süre belirsiz"} saat · devir ${row.deferredQuantity ?? "belirsiz"} ${row.unit ?? ""} · ${schedule} · ${distribution}${row.note ? ` · ${row.note}` : ""}`;
}

import type { WeeklyPlanningViewProps } from "./WeeklyPlanningView";

type Props = {
  props: WeeklyPlanningViewProps;
  busy: boolean;
  activationReady: boolean;
};

export function WeeklyPlanningActivationPanel({
  props,
  busy,
  activationReady,
}: Props) {
  const preview = props.activationPreview;
  if (!props.activationConfirmationOpen || !preview) return null;
  return (
    <section
      className="weekly-planning-activation"
      role="region"
      aria-labelledby="weekly-plan-activation-heading"
    >
      <div className="weekly-planning-activation-copy">
        <AlertTriangle size={19} />
        <div>
          <h3 id="weekly-plan-activation-heading">
            Planlama onayı: önceki ve sonraki
          </h3>
          <p>
            Onay yalnız bu revizyona ve karşılaştırmaya bağlıdır. Mikro'ya kayıt
            yazılmaz.
          </p>
          <p>
            Çalışma biçimi: {preview.executionMode === "OPERATOR_SELECTED"
              ? "Operatör doğrulanmış iş emrini Excel satır sırasındaki haftalık listeden seçer; kapasite ve saat üretilmez."
              : "Kapasite ve saat planına göre sıralı üretim."}
          </p>
          <div className="weekly-planning-compare-summary">
            <span>
              <small>ÖNCEKİ</small>
              <strong>{preview.baseline.rowCount} satır</strong>
            </span>
            <span>
              <small>SONRAKİ</small>
              <strong>{preview.draft.includedRowCount} hazır satır</strong>
            </span>
            <span>
              <small>AKTARILMAYACAK</small>
              <strong>{preview.draft.excludedRowCount} satır</strong>
            </span>
          </div>
          <p>
            Gün başlangıcı: etkin plan{" "}
            {preview.calendar.baselineDayStartsAt ?? "yok"}, Excel kaynağı{" "}
            {preview.calendar.sourceDayStartsAt ?? "yok"}, onaylanacak taslak{" "}
            {preview.calendar.draftDayStartsAt}.
          </p>
          {preview.changes.length ? (
            <details>
              <summary>
                Etkin plana etkisi olan {preview.changes.length} değişikliği
                göster
              </summary>
              <ul>
                {preview.changes.map((change, index) => (
                  <li
                    key={`${change.type}-${change.after?.rowId ?? change.before?.rowId ?? index}`}
                  >
                    <strong>
                      {change.after?.productName ?? change.before?.productName}
                    </strong>
                    <span>Önce: {comparisonLine(change.before)}</span>
                    <span>Sonra: {comparisonLine(change.after)}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {preview.sourceChanges.length ? (
            <details>
              <summary>
                Excel kaynağından değiştirilen {preview.sourceChanges.length}{" "}
                satırı göster
              </summary>
              <ul>
                {preview.sourceChanges.map((change) => (
                  <li key={change.after.rowId}>
                    <strong>{change.after.productName}</strong>
                    <span>Kaynak: {comparisonLine(change.before)}</span>
                    <span>Taslak: {comparisonLine(change.after)}</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
          {preview.excludedRows.length ? (
            <>
              <details open>
                <summary>
                  Aktarılmayacak {preview.excludedRows.length} satır
                </summary>
                <ul>
                  {preview.excludedRows.map((row) => (
                    <li key={row.rowId}>
                      <strong>{row.productName}</strong>
                      <span>
                        {row.lineName} · {row.reasons.join(" ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </details>
              <label className="weekly-planning-ack">
                <input
                  type="checkbox"
                  checked={props.excludedRowsAcknowledged}
                  disabled={busy}
                  onChange={(event) =>
                    props.onExcludedRowsAcknowledgedChange(event.target.checked)
                  }
                />
                <span>
                  Eksik ve bağlı satırların bu onayda üretim kuyruğuna
                  aktarılmayacağını gördüm.
                </span>
              </label>
            </>
          ) : null}
        </div>
      </div>
      <div className="weekly-planning-activation-actions">
        <button
          type="button"
          disabled={busy}
          onClick={props.onCancelActivation}
        >
          Vazgeç
        </button>
        <button
          type="button"
          className="primary-button"
          disabled={
            !activationReady ||
            busy ||
            (preview.excludedRows.length > 0 && !props.excludedRowsAcknowledged)
          }
          onClick={props.onActivate}
        >
          {props.activating
            ? "Etkinleştiriliyor…"
            : "Planlamanın onayıyla etkinleştir"}
        </button>
      </div>
    </section>
  );
}
